import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ClipboardList, Inbox, Trophy, PhoneCall, MessageSquare, XCircle, Loader2, RefreshCw, Percent, Download,
} from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const WINDOWS = [
  { months: 6, label: "6 meses" },
  { months: 12, label: "12 meses" },
];

function MatrixTable({ title, icon: Icon, accent, block, months, monthLabels, testid }) {
  const rows = block?.rows || [];
  return (
    <div className="overflow-x-auto rounded-xl border border-black/5 bg-card p-4 shadow-sm" data-testid={testid}>
      <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold">
        <Icon className="h-4 w-4" style={{ color: accent }} /> {title}
        <span className="ml-auto text-sm font-normal text-muted-foreground">
          Total: <strong style={{ color: accent }}>{block?.grand_total ?? 0}</strong>
        </span>
      </h2>
      <table className="w-full min-w-[560px] text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="py-1.5 pr-3 text-left font-medium">Agente</th>
            {months.map((m) => <th key={m} className="px-2 py-1.5 text-right font-medium">{monthLabels[m]}</th>)}
            <th className="py-1.5 pl-3 text-right font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.agent_name} className="border-t border-black/5">
              <td className="py-1.5 pr-3 font-medium">{r.agent_name}</td>
              {months.map((m) => (
                <td key={m} className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{r.months[m] || 0}</td>
              ))}
              <td className="py-1.5 pl-3 text-right font-bold tabular-nums" style={{ color: accent }}>{r.total}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-black/10 font-semibold">
            <td className="py-1.5 pr-3">Totales</td>
            {months.map((m) => <td key={m} className="px-2 py-1.5 text-right tabular-nums">{block?.totals_by_month?.[m] || 0}</td>)}
            <td className="py-1.5 pl-3 text-right tabular-nums" style={{ color: accent }}>{block?.grand_total ?? 0}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function pct(rate) {
  return rate == null ? "—" : `${Math.round(rate * 100)}%`;
}

function ConversionTable({ block, months, monthLabels }) {
  const accent = "#3E6B8B";
  const rows = block?.rows || [];
  const tb = block?.totals_by_month || {};
  return (
    <div className="overflow-x-auto rounded-xl border border-black/5 bg-card p-4 shadow-sm" data-testid="resumen-conversion">
      <h2 className="mb-1 flex items-center gap-2 font-heading text-base font-semibold">
        <Percent className="h-4 w-4" style={{ color: accent }} /> Tasa de conversión
        <span className="ml-auto text-sm font-normal text-muted-foreground">
          Global: <strong style={{ color: accent }}>{pct(block?.overall?.rate)}</strong>
          {block?.overall && <span className="ml-1 text-xs">({block.overall.sold}/{block.overall.received})</span>}
        </span>
      </h2>
      <p className="mb-3 text-[11px] text-muted-foreground">
        De los leads recibidos cada mes, % que acabaron en venta. Los meses recientes salen bajos porque esos leads aún no han tenido tiempo de convertir.
      </p>
      <table className="w-full min-w-[560px] text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="py-1.5 pr-3 text-left font-medium">Agente</th>
            {months.map((m) => <th key={m} className="px-2 py-1.5 text-right font-medium">{monthLabels[m]}</th>)}
            <th className="py-1.5 pl-3 text-right font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.agent_name} className="border-t border-black/5">
              <td className="py-1.5 pr-3 font-medium">{r.agent_name}</td>
              {months.map((m) => {
                const c = r.months[m] || {};
                return (
                  <td key={m} className="px-2 py-1.5 text-right tabular-nums">
                    <div className={c.received ? "font-semibold" : "text-muted-foreground"}>{pct(c.rate)}</div>
                    {c.received ? <div className="text-[10px] text-muted-foreground">{c.sold}/{c.received}</div> : null}
                  </td>
                );
              })}
              <td className="py-1.5 pl-3 text-right tabular-nums">
                <div className="font-bold" style={{ color: accent }}>{pct(r.total.rate)}</div>
                <div className="text-[10px] text-muted-foreground">{r.total.sold}/{r.total.received}</div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-black/10 font-semibold">
            <td className="py-1.5 pr-3">Totales</td>
            {months.map((m) => (
              <td key={m} className="px-2 py-1.5 text-right tabular-nums">{pct(tb[m]?.rate)}</td>
            ))}
            <td className="py-1.5 pl-3 text-right tabular-nums" style={{ color: accent }}>{pct(block?.overall?.rate)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function LostSection({ lost, months, monthLabels }) {
  const accent = "#B23B3B";
  const stages = lost?.stage_keys || [];
  const labels = lost?.stage_labels || {};
  const maxStage = Math.max(1, ...(lost?.by_stage || []).map((s) => s.count));
  return (
    <div className="space-y-4" data-testid="resumen-lost">
      <div className="overflow-x-auto rounded-xl border border-black/5 bg-card p-4 shadow-sm">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold">
          <XCircle className="h-4 w-4" style={{ color: accent }} /> Leads perdidos — en qué fase se perdieron
          <span className="ml-auto text-sm font-normal text-muted-foreground">
            Total: <strong style={{ color: accent }}>{lost?.grand_total ?? 0}</strong>
          </span>
        </h2>

        {/* Distribución por fase */}
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="resumen-lost-by-stage">
          {(lost?.by_stage || []).map((s) => (
            <div key={s.stage} className="rounded-lg border border-black/5 bg-secondary/40 p-3">
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
                <span className="font-heading text-lg font-bold" style={{ color: accent }}>{s.count}</span>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
                <div className="h-full rounded-full" style={{ width: `${(s.count / maxStage) * 100}%`, background: accent }} />
              </div>
            </div>
          ))}
        </div>

        {/* Por agente y fase */}
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="py-1.5 pr-3 text-left font-medium">Agente</th>
              {stages.map((s) => <th key={s} className="px-2 py-1.5 text-right font-medium">{labels[s]}</th>)}
              <th className="py-1.5 pl-3 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {(lost?.by_agent || []).map((r) => (
              <tr key={r.agent_name} className="border-t border-black/5">
                <td className="py-1.5 pr-3 font-medium">{r.agent_name}</td>
                {stages.map((s) => <td key={s} className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{r.by_stage[s] || 0}</td>)}
                <td className="py-1.5 pl-3 text-right font-bold tabular-nums" style={{ color: accent }}>{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Perdidos por mes */}
      <MatrixTable
        title="Leads perdidos por mes"
        icon={XCircle}
        accent={accent}
        block={lost?.monthly}
        months={months}
        monthLabels={monthLabels}
        testid="resumen-lost-monthly"
      />
    </div>
  );
}

function KpiStrip({ data }) {
  const items = [
    { icon: Inbox, label: "Leads recibidos", value: data.leads_received?.grand_total ?? 0, color: "#34406B" },
    { icon: Trophy, label: "Ventas", value: data.sales?.grand_total ?? 0, color: "#244532" },
    { icon: Percent, label: "Conversión", value: pct(data.conversion?.overall?.rate), color: "#3E6B8B" },
    { icon: PhoneCall, label: "Llamadas", value: data.calls?.grand_total ?? 0, color: "#6B4F20" },
    { icon: MessageSquare, label: "Follow ups", value: data.followups?.grand_total ?? 0, color: "#C45A45" },
    { icon: XCircle, label: "Perdidos", value: data.lost?.grand_total ?? 0, color: "#B23B3B" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="resumen-kpis">
      {items.map((it) => (
        <div key={it.label} className="rounded-xl border border-black/5 bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <it.icon className="h-3.5 w-3.5" style={{ color: it.color }} /> {it.label}
          </div>
          <div className="mt-1.5 font-heading text-2xl font-bold" style={{ color: it.color }}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}

export default function SupervisionPage() {
  const { user } = useAuth();
  const [months, setMonths] = useState(6);
  const q = useQuery({
    queryKey: ["resumen-overview", months],
    queryFn: async () => (await api.get(`/zicasso/supervision/overview?months=${months}`)).data,
    refetchInterval: 300_000,
    enabled: user?.is_admin === true,
  });
  const syncLost = useMutation({
    mutationFn: () => api.post(`/zicasso/supervision/sync-lost?months=${months}`),
    onSuccess: (r) => { toast.success(`${r.data?.synced ?? 0} leads perdidos actualizados`); q.refetch(); },
    onError: () => toast.error("No se pudieron actualizar los leads perdidos"),
  });

  const [exporting, setExporting] = useState(false);
  const downloadExcel = async () => {
    try {
      setExporting(true);
      const res = await api.get(`/zicasso/supervision/export?months=${months}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "Resumen_Zicasso.xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Resumen descargado");
    } catch {
      toast.error("No se pudo descargar el Resumen");
    } finally {
      setExporting(false);
    }
  };

  if (user && !user.is_admin) return <Navigate to="/app/leads" replace />;
  const data = q.data;
  const mk = data?.months || [];
  const ml = data?.month_labels || {};

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="resumen-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold">
            <ClipboardList className="h-6 w-6 text-[#34406B]" /> Resumen
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Actividad del equipo por mes · {data?.generated_at ? new Date(data.generated_at).toLocaleString("es-ES") : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => syncLost.mutate()} disabled={syncLost.isPending} data-testid="resumen-sync-lost">
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncLost.isPending ? "animate-spin" : ""}`} />
            Actualizar perdidos
          </Button>
          <Button size="sm" onClick={downloadExcel} disabled={exporting || !data} data-testid="resumen-export-excel">
            {exporting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
            Descargar Excel
          </Button>
          <div className="flex items-center gap-1 rounded-lg border border-black/5 bg-card p-1" data-testid="resumen-window">
            {WINDOWS.map((w) => (
              <button
                key={w.months}
                onClick={() => setMonths(w.months)}
                data-testid={`resumen-window-${w.months}`}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  months === w.months ? "bg-[#34406B] text-white" : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {q.isLoading || !data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[...Array(5)].map((_, i) => <Skeleton key={`k-${i}`} className="h-20 w-full rounded-xl" />)}
          </div>
          {[...Array(3)].map((_, i) => <Skeleton key={`t-${i}`} className="h-48 w-full rounded-xl" />)}
        </div>
      ) : (
        <>
          <KpiStrip data={data} />
          <MatrixTable title="Leads recibidos" icon={Inbox} accent="#34406B" block={data.leads_received} months={mk} monthLabels={ml} testid="resumen-leads" />
          <MatrixTable title="Ventas" icon={Trophy} accent="#244532" block={data.sales} months={mk} monthLabels={ml} testid="resumen-sales" />
          <ConversionTable block={data.conversion} months={mk} monthLabels={ml} />
          <MatrixTable title="Llamadas hechas" icon={PhoneCall} accent="#6B4F20" block={data.calls} months={mk} monthLabels={ml} testid="resumen-calls" />
          <MatrixTable title="Follow ups hechos" icon={MessageSquare} accent="#C45A45" block={data.followups} months={mk} monthLabels={ml} testid="resumen-followups" />
          <LostSection lost={data.lost} months={mk} monthLabels={ml} />

          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            {q.isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
            Leads recibidos por fecha de creación en Zicasso; ventas por fecha de cierre; llamadas y follow-ups según los mensajes sincronizados de Zicasso; perdidos = leads cerrados sin venta (INACTIVE).
          </p>
        </>
      )}
    </div>
  );
}
